import { FlashbarProps } from "@amzn/awsui-components-console";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";

export interface NotificationsContextType {
  items: FlashbarProps.MessageDefinition[];
  notifySuccess: (content: string | ReactNode) => void;
  notifyError: (header: string, content: string | ReactNode) => void;
}

const NotificationsContext = createContext<
  NotificationsContextType | undefined
>(undefined);

interface NotificationsProviderProps {
  children: React.ReactNode;
}

export const NotificationsProvider: React.FC<NotificationsProviderProps> = ({
  children,
}) => {
  const [items, setItems] = useState<FlashbarProps.MessageDefinition[]>([]);

  const notifySuccess = useCallback((content: string | ReactNode) => {
    setItems((currentItems) => {
      const id = `message_success_${currentItems.length + 1}`;
      return [
        ...currentItems,
        {
          type: "success",
          dismissible: true,
          dismissLabel: "Dismiss message",
          id,
          content,
          onDismiss: () =>
            setItems((items) => items.filter((item) => item.id !== id)),
        },
      ];
    });
  }, []);

  const notifyError = useCallback(
    (header: string, content: string | ReactNode) => {
      setItems((currentItems) => {
        const id = `message_error_${currentItems.length + 1}`;
        return [
          ...currentItems,
          {
            type: "error",
            dismissible: true,
            dismissLabel: "Dismiss message",
            id,
            header,
            content,
            onDismiss: () =>
              setItems((items) => items.filter((item) => item.id !== id)),
          },
        ];
      });
    },
    []
  );

  return (
    <NotificationsContext.Provider
      value={{
        items,
        notifySuccess,
        notifyError,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error(
      "useNotifications must be used within a NotificationsProvider"
    );
  }
  return context;
};
